/**
 * Wallet thresholds, shared by everything that renders a balance.
 *
 * This lived inside `pages/billing/index.tsx`, so the sidebar chip and the
 * billing page would have disagreed about what "running low" means the moment
 * either one changed. One definition.
 */
const LOW_BALANCE_MINOR: Record<string, number> = {
  ZAR: 5000,
  NGN: 5_000_00,
  KES: 500_00,
  USD: 300,
  EUR: 300,
  GBP: 250
};

/**
 * Roughly a week of light sending, per currency. A flat 5000 minor units would
 * be R50 in ZAR and ₦50 in NGN, which is effectively zero.
 */
export function lowThreshold(currency: string): number {
  return LOW_BALANCE_MINOR[currency.toUpperCase()] ?? 5000;
}

export type WalletState = 'empty' | 'low' | 'ok' | 'extra';

/** What the wallet says about extra use, when it is switched on (Growth and Scale, 30 October 2026). */
export interface ExtraUseSummary {
  active?: boolean | null;
}

/**
 * At zero, sending is already blocked rather than "running low", so the two
 * states are kept apart: they need different words and different urgency.
 *
 * The billing service now decides the line (the tenant's own threshold under
 * Billing → Alerts, or the currency default) and returns `state` on the
 * wallet; when it is present it wins, so the sidebar, the billing page and
 * the alert emails agree. The local table is the fallback for an older
 * service.
 *
 * `extra` (30 October 2026): the credit has run out but extra use is active, so
 * everything keeps working up to the business's monthly limit. It is not an
 * error, and the console says so calmly.
 */
export function walletState(
  creditsMinor: number,
  currency: string,
  serverState?: string | null,
  extraUse?: ExtraUseSummary | null
): WalletState {
  if (extraUse?.active && creditsMinor <= 0) return 'extra';
  if (serverState === 'empty' || serverState === 'low' || serverState === 'ok') return serverState;
  if (creditsMinor <= 0) return 'empty';
  return creditsMinor < lowThreshold(currency) ? 'low' : 'ok';
}

/**
 * The balance as it is shown. While extra use is active the wallet can go below
 * zero (that is what is owed this month), and the console shows extra use
 * instead of a negative balance, so the balance itself reads as zero.
 */
export function shownBalance(creditsMinor: number, extraUse?: ExtraUseSummary | null): number {
  return extraUse?.active ? Math.max(0, creditsMinor) : creditsMinor;
}
